import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "bootstrap/dist/css/bootstrap.min.css";
import Header from '../components/Header'
import Footer from '../components/Footer'

const RegisterForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    password: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      toast.error("Name is required");
      return false;
    }
    if (!formData.email.trim()) {
      toast.error("Email is required");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error("Please enter a valid email");
      return false;
    }
    if (!formData.mobile.trim()) {
      toast.error("Mobile number is required");
      return false;
    }
    if (!/^\d{10}$/.test(formData.mobile)) {
      toast.error("Please enter a valid 10-digit mobile number");
      return false;
    }
    if (!formData.password) {
      toast.error("Password is required");
      return false;
    }
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/auth/signup/`,
        {
          name: formData.name,
          email: formData.email,
          mobile: formData.mobile,
          password: formData.password,
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.status === 200) {
        const userDetail = response.data.data.user_detail;
        
        // ✅ Store all user details
        localStorage.setItem('user', JSON.stringify(userDetail));
        localStorage.setItem('userId', userDetail.id);
        localStorage.setItem('user_id', userDetail.user_id);
        localStorage.setItem('userName', userDetail.name);
        localStorage.setItem('userEmail', userDetail.email);
        localStorage.setItem('userMobile', userDetail.mobile);
        
        // ✅ Create session token
        localStorage.setItem('sessionToken', `session_${userDetail.id}_${Date.now()}`);
        localStorage.setItem('isAuthenticated', 'true');
        
        toast.success('Signup successful! Redirecting to login...');
        setTimeout(() => {
          navigate('/login');
        }, 1500);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Signup failed. Please try again.';
      toast.error(errorMsg);
      console.error('Signup error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Header />

      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          padding: 0;
          font-family: 'Poppins', sans-serif;
          margin-top: 150px;
        }

        .register-container {
          min-height: calc(100vh - 200px);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
        }

        .register-card {
          background: white;
          border-radius: 15px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          overflow: hidden;
          max-width: 400px;
          width: 100%;
        }

        .register-header {
          background: linear-gradient(135deg, #6B2A00 0%, #8B3A00 100%);
          color: white;
          padding: 35px 20px;
          text-align: center;
        }

        .register-header h1 {
          font-size: 28px;
          font-weight: 700;
          margin: 0;
          font-family: 'Poppins', sans-serif;
        }

        .register-header p {
          margin: 8px 0 0 0;
          font-size: 13px;
          opacity: 0.85;
          font-weight: 500;
        }

        .register-body {
          padding: 35px;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-group label {
          display: block;
          margin-bottom: 8px;
          color: #6B2A00;
          font-weight: 600;
          font-size: 13px;
          letter-spacing: 0.3px;
        }

        .form-group input {
          width: 100%;
          padding: 11px 13px;
          border: 2px solid #E8E8E8;
          border-radius: 8px;
          font-size: 13px;
          transition: all 0.3s ease;
          font-family: 'Poppins', sans-serif;
        }

        .form-group input:focus {
          outline: none;
          border-color: #E3952A;
          box-shadow: 0 0 0 3px rgba(227, 149, 42, 0.08);
        }

        .input-wrapper {
          position: relative;
        }

        .password-toggle {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          cursor: pointer;
          color: #6B2A00;
          font-size: 15px;
          background: none;
          border: none;
          padding: 5px;
          opacity: 0.7;
          transition: opacity 0.2s ease;
        }

        .password-toggle:hover {
          opacity: 1;
        }

        .submit-btn {
          width: 100%;
          padding: 12px;
          background: linear-gradient(135deg, #6B2A00 0%, #8B3A00 100%);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s ease;
          text-transform: uppercase;
          letter-spacing: 0.8px;
          margin-top: 8px;
        }

        .submit-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 10px 25px rgba(107, 42, 0, 0.25);
        }

        .submit-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .spinner-border {
          width: 14px !important;
          height: 14px !important;
          margin-right: 6px;
        }

        .divider {
          text-align: center;
          margin: 20px 0;
          position: relative;
          color: #999;
          font-size: 12px;
          font-weight: 500;
        }

        .divider::before,
        .divider::after {
          content: '';
          position: absolute;
          top: 50%;
          width: 45%;
          height: 1px;
          background: #E0E0E0;
        }

        .divider::before {
          left: 0;
        }

        .divider::after {
          right: 0;
        }

        .login-section {
          text-align: center;
          margin: 18px 0;
          padding: 0;
          color: #666;
          font-size: 13px;
        }

        .login-section a {
          color: #E3952A;
          text-decoration: none;
          font-weight: 700;
          transition: color 0.3s ease;
        }

        .login-section a:hover {
          color: #6B2A00;
        }

        .register-footer {
          text-align: center;
          padding: 15px 35px 30px;
          border-top: 1px solid #f0f0f0;
        }

        .register-footer p {
          color: #666;
          font-size: 12px;
          margin: 0;
        }

        .register-footer a {
          color: #E3952A;
          text-decoration: none;
          font-weight: 600;
          font-size: 13px;
          transition: color 0.3s ease;
        }

        .register-footer a:hover {
          color: #6B2A00;
        }

        @media (max-width: 576px) {
          .register-container {
            padding: 20px 15px;
            min-height: calc(100vh - 150px);
          }

          .register-card {
            max-width: 100%;
          }

          .register-body {
            padding: 25px;
          }

          .register-header {
            padding: 25px 15px;
          }

          .register-header h1 {
            font-size: 24px;
          }
        }
      `}</style>

      <div className="register-container">
        <div className="register-card">
          <div className="register-header">
            <h1>🙏 Ez Puja Path</h1>
            <p>Create Your Account</p>
          </div>

          <div className="register-body">
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Full Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  required
                  disabled={isLoading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  required
                  disabled={isLoading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="mobile">Mobile Number</label>
                <input
                  type="tel"
                  id="mobile"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  placeholder="Enter 10-digit mobile number"
                  maxLength="10"
                  required
                  disabled={isLoading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <div className="input-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Create a password (min 6 characters)"
                    required
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isLoading}
                  >
                    <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>
                </div>
              </div>

              <button type="submit" className="submit-btn" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    Creating Account...
                  </>
                ) : (
                  'SIGN UP'
                )}
              </button>
            </form>

            <div className="divider">OR</div>

            <div className="login-section">
              Already have an account?{' '}
              <a href="/login">Login here</a>
            </div>

            <div className="register-footer">
              <p>By signing up, you agree to our Terms & Conditions</p>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      <Footer />
    </>
  );
};

export default RegisterForm;
